import React, { useState, useEffect } from "react";
import apiClient from "../../../../../api/apiClient";
import { CITIES_BY_STATE, normalizeCityField } from "../shared/usCities";
import { LEAD_PRIORITIES as PRIORITIES, LEAD_STATUSES as STATUSES, MANUAL_LEAD_SOURCES, leadSourceLabel } from "../shared/crmConstants";
import { PARTNER_TYPES } from "../../../../../constants/supplyChain";
import "./LeadFormModal.css";

const US_STATES = [
    { abbr: "AL", name: "Alabama" }, { abbr: "AK", name: "Alaska" },
    { abbr: "AZ", name: "Arizona" }, { abbr: "AR", name: "Arkansas" },
    { abbr: "CA", name: "California" }, { abbr: "CO", name: "Colorado" },
    { abbr: "CT", name: "Connecticut" }, { abbr: "DE", name: "Delaware" },
    { abbr: "FL", name: "Florida" }, { abbr: "GA", name: "Georgia" },
    { abbr: "HI", name: "Hawaii" }, { abbr: "ID", name: "Idaho" },
    { abbr: "IL", name: "Illinois" }, { abbr: "IN", name: "Indiana" },
    { abbr: "IA", name: "Iowa" }, { abbr: "KS", name: "Kansas" },
    { abbr: "KY", name: "Kentucky" }, { abbr: "LA", name: "Louisiana" },
    { abbr: "ME", name: "Maine" }, { abbr: "MD", name: "Maryland" },
    { abbr: "MA", name: "Massachusetts" }, { abbr: "MI", name: "Michigan" },
    { abbr: "MN", name: "Minnesota" }, { abbr: "MS", name: "Mississippi" },
    { abbr: "MO", name: "Missouri" }, { abbr: "MT", name: "Montana" }, 
    { abbr: "NE", name: "Nebraska" }, { abbr: "NV", name: "Nevada" },
    { abbr: "NH", name: "New Hampshire" }, { abbr: "NJ", name: "New Jersey" },
    { abbr: "NM", name: "New Mexico" }, { abbr: "NY", name: "New York" },
    { abbr: "NC", name: "North Carolina" }, { abbr: "ND", name: "North Dakota" },
    { abbr: "OH", name: "Ohio" }, { abbr: "OK", name: "Oklahoma" },
    { abbr: "OR", name: "Oregon" }, { abbr: "PA", name: "Pennsylvania" },
    { abbr: "RI", name: "Rhode Island" }, { abbr: "SC", name: "South Carolina" },
    { abbr: "SD", name: "South Dakota" }, { abbr: "TN", name: "Tennessee" },
    { abbr: "TX", name: "Texas" }, { abbr: "UT", name: "Utah" },
    { abbr: "VT", name: "Vermont" }, { abbr: "VA", name: "Virginia" },
    { abbr: "WA", name: "Washington" }, { abbr: "WV", name: "West Virginia" },
    { abbr: "WI", name: "Wisconsin" }, { abbr: "WY", name: "Wyoming" },
    { abbr: "DC", name: "District of Columbia" },
];

const STATE_NAME_TO_ABBR = Object.fromEntries(
    US_STATES.map(s => [s.name.toLowerCase(), s.abbr])
);
function normalizeStateField(val) {
    if (!val) return "";
    if (val.length === 2) return val.toUpperCase();
    return STATE_NAME_TO_ABBR[val.trim().toLowerCase()] || val;
}

const EMPTY = {
    firstName: "", lastName: "", email: "", phone: "", jobTitle: "", company: "",
    industry: "", website: "", linkedIn: "", address: "", city: "", state: "", zipCode: "",
    stageId: "", status: "new", priority: "cold", source: "other",
    estimatedValue: "", estimatedCloseDate: "", assignedTo: "", notes: "",
    businessType: "", usdaLicenseNumber: "", ein: "",
};

const Req = () => <span className="lf-req" aria-hidden="true">*</span>;
const Section = ({ label }) => <div className="lf-section">{label}</div>;

const LeadFormModal = ({ lead, stages, admins, onClose, onSaved, baseEndpoint = "/admin/crm/leads" }) => {
    const [form, setForm] = useState(lead
        ? { ...EMPTY, ...lead, stageId: lead.stageId || "", state: normalizeStateField(lead.state) }
        : { ...EMPTY });
    const [saving, setSaving] = useState(false);
    const [loadingFull, setLoadingFull] = useState(!!lead);
    const [error, setError] = useState("");

    // Fetch full lead record — the list endpoint omits city, state, notes, etc.
    useEffect(() => {
        if (!lead) return;
        let cancelled = false;
        apiClient.get(`${baseEndpoint}/${lead.id}`).then(res => {
            if (cancelled) return;
            const full = res.data.lead || res.data;
            const state = normalizeStateField(full.state);
            const city = normalizeCityField(full.city, state);
            setForm({ ...EMPTY, ...full, stageId: full.stageId || "", state, city });
            setLoadingFull(false);
        }).catch(() => {
            if (!cancelled) setLoadingFull(false);
        });
        return () => { cancelled = true; };
    }, [lead?.id]);

    useEffect(() => {
        if (stages.length && !form.stageId) {
            const def = stages.find(s => s.isDefault) || stages[0];
            setForm(f => ({ ...f, stageId: def?.id || "" }));
        }
    }, [stages]);

    const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));

    // When state changes, reset city so the dropdown re-initialises correctly
    const handleStateChange = e => {
        setForm(f => ({ ...f, state: e.target.value, city: "" }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = { ...form };
            ["firstName","lastName","email","phone","jobTitle","industry","website","linkedIn","address","city","state","zipCode",
             "estimatedValue","estimatedCloseDate","assignedTo","notes","businessType","usdaLicenseNumber","ein"].forEach(k => {
                if (payload[k] === "") payload[k] = null;
            });
            // Server-managed / read-only fields from the full record are never sent back.
            ["nameKey","dedupeKey","activeCitationCount","unverifiedCitationCount","convertedLicenseNo","licenseLinkedAt",
             "legacyProspectId","stageName","stageColor","createdBy","createdAt","updatedAt","deletedAt","details"].forEach(k => delete payload[k]);
            if (payload.estimatedValue) payload.estimatedValue = parseFloat(payload.estimatedValue);

            if (lead) {
                await apiClient.put(`${baseEndpoint}/${lead.id}`, payload);
            } else {
                await apiClient.post(baseEndpoint, payload);
            }
            onSaved();
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || "Failed to save lead");
        } finally {
            setSaving(false);
        }
    };

    const citiesForState = form.state ? (CITIES_BY_STATE[form.state] || []) : [];

    return (
        <div className="crm-modal-overlay lf-overlay-mobile" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="crm-modal crm-modal--lead-form">
                <div className="crm-modal__header">
                    <h2 className="crm-modal__title">{lead ? "Edit Lead" : "New Lead"}</h2>
                    <button className="crm-modal__close" onClick={onClose} type="button">×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="crm-modal__body">

                        {loadingFull && (
                            <div className="lf-loading">
                                <span className="lf-loading__spinner" />
                                Loading lead data…
                            </div>
                        )}
                        {error && <div className="lf-error">{error}</div>}

                        {/* ── Business ── a lead is an operator business; the contact person is optional */}
                        <Section label="Business" />
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-company">Company <Req /></label>
                                <input id="lf-company" className="admin-input" required value={form.company || ""} onChange={set("company")} maxLength={200} />
                            </div>
                            <div className="admin-form-group">
                                <label htmlFor="lf-businessType">Business type</label>
                                <select id="lf-businessType" className="admin-input" value={form.businessType || ""} onChange={set("businessType")}>
                                    <option value="">—</option>
                                    {PARTNER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-usda">USDA hemp license #</label>
                                <input id="lf-usda" className="admin-input" value={form.usdaLicenseNumber || ""} onChange={set("usdaLicenseNumber")} maxLength={50} />
                            </div>
                            <div className="admin-form-group">
                                <label htmlFor="lf-ein">EIN</label>
                                <input id="lf-ein" className="admin-input" value={form.ein || ""} onChange={set("ein")} maxLength={20} placeholder="12-3456789" />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-industry">Industry</label>
                                <input id="lf-industry" className="admin-input" value={form.industry || ""} onChange={set("industry")} maxLength={100} />
                            </div>
                            <div className="admin-form-group">
                                <label htmlFor="lf-source">Lead Source</label>
                                {form.source === "supply_chain" ? (
                                    <input id="lf-source" className="admin-input" value={leadSourceLabel("supply_chain")} readOnly />
                                ) : (
                                    <select id="lf-source" className="admin-input" value={form.source} onChange={set("source")}>
                                        {MANUAL_LEAD_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-website">Website</label>
                                <input id="lf-website" className="admin-input" type="url" placeholder="https://"
                                    value={form.website || ""} onChange={set("website")} maxLength={500} />
                            </div>
                            <div className="admin-form-group">
                                <label htmlFor="lf-linkedin">LinkedIn</label>
                                <input id="lf-linkedin" className="admin-input" type="url" placeholder="https://linkedin.com/in/…"
                                    value={form.linkedIn || ""} onChange={set("linkedIn")} maxLength={500} />
                            </div>
                        </div>

                        {/* ── Contact (optional) ── */}
                        <Section label="Contact (optional)" />
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-first">First Name</label>
                                <input id="lf-first" className="admin-input" value={form.firstName || ""} onChange={set("firstName")} maxLength={100} />
                            </div>
                            <div className="admin-form-group">
                                <label htmlFor="lf-last">Last Name</label>
                                <input id="lf-last" className="admin-input" value={form.lastName || ""} onChange={set("lastName")} maxLength={100} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-email">Email</label>
                                <input id="lf-email" className="admin-input" type="email" value={form.email || ""} onChange={set("email")} />
                            </div>
                            <div className="admin-form-group">
                                <label htmlFor="lf-phone">Phone</label>
                                <input id="lf-phone" className="admin-input" value={form.phone || ""} onChange={set("phone")} maxLength={50} placeholder="+1 (555) 000-0000" />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label htmlFor="lf-jobtitle">Job Title</label>
                                <input id="lf-jobtitle" className="admin-input" value={form.jobTitle || ""} onChange={set("jobTitle")} maxLength={150} />
                            </div>
                        </div>

                        {/* ── Pipeline & Deal ── */}
                        <Section label="Pipeline & Deal" />
                        <div className="admin-form-row admin-form-row--3">
                            <div className="admin-form-group">
                                <label>Stage <Req /></label>
                                <select className="admin-input" required value={form.stageId} onChange={set("stageId")}>
                                    <option value="">Select stage…</option>
                                    {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Status</label>
                                <select className="admin-input" value={form.status} onChange={set("status")}>
                                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Priority</label>
                                <select className="admin-input" value={form.priority} onChange={set("priority")}>
                                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="admin-form-row admin-form-row--3">
                            <div className="admin-form-group">
                                <label>Assigned To</label>
                                <select className="admin-input" value={form.assignedTo} onChange={set("assignedTo")}>
                                    <option value="">Unassigned</option>
                                    {admins.map(a => (
                                        <option key={a.userId} value={a.userId}>
                                            {a.firstName} {a.lastName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>Est. Value ($)</label>
                                <input className="admin-input" type="number" min="0" step="0.01" placeholder="0.00"
                                    value={form.estimatedValue} onChange={set("estimatedValue")} />
                            </div>
                            <div className="admin-form-group">
                                <label>Est. Close Date</label>
                                <input className="admin-input" type="date"
                                    value={form.estimatedCloseDate ? form.estimatedCloseDate.slice(0, 10) : ""}
                                    onChange={set("estimatedCloseDate")} />
                            </div>
                        </div>

                        {/* ── Location ── */}
                        <Section label="Location" />
                        <div className="lf-location-row">
                            <div className="admin-form-group">
                                <label>State</label>
                                <select className="admin-input" value={form.state} onChange={handleStateChange}>
                                    <option value="">— Select state —</option>
                                    {US_STATES.map(s => (
                                        <option key={s.abbr} value={s.abbr}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label>City</label>
                                {form.state ? (
                                    <select className="admin-input" value={form.city} onChange={set("city")}>
                                        <option value="">— Select city —</option>
                                        {citiesForState.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="lf-city-placeholder">Select a state first</div>
                                )}
                            </div>
                        </div>

                        {/* ── Notes ── */}
                        <Section label="Notes" />
                        <div className="admin-form-group">
                            <textarea
                                className="admin-input"
                                rows={3}
                                value={form.notes}
                                onChange={set("notes")}
                                maxLength={10000}
                                placeholder="Add any relevant notes about this lead…"
                                style={{ resize: "vertical" }}
                            />
                        </div>

                    </div>

                    <div className="crm-modal__footer">
                        <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="admin-btn admin-btn--primary" disabled={saving || loadingFull}>
                            {saving ? "Saving…" : lead ? "Save Changes" : "Create Lead"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LeadFormModal;
